import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { PButton } from "../ui/PButton";
import { PrimeGlyph } from "../ui/PrimeGlyph";
import { useToast } from "../ui/toast";
import { useDeleteSavedFilter, useSavedFilters } from "./api";
import { ConfirmPopup } from "./ConfirmPopup";
import { parseSavedFilter, withFilters, type AdvancedFilter, type SavedFilter } from "./filter-logic";
import "./filters.css";

// PORT OF `my-filters`: THE USER'S SAVED FILTERS, ONE `p-4 mt-3` CARD EACH (A `tune` ICON AND
// THE UPPERCASED NAME, THEN APPLY AND A DANGER DELETE), OR "NO SAVED FILTERS AVAILABLE". DELETE
// ASKS FIRST IN A `p-confirmPopup` WITH A BIG RED `pi-exclamation-circle`.
export function MyFilters(props: { onApply: (f: AdvancedFilter) => void }) {
  const { onApply } = props;
  const toast = useToast();
  const saved = useSavedFilters();
  const remove = useDeleteSavedFilter();
  const [pending, setPending] = useState<{ anchor: HTMLElement; filter: SavedFilter } | null>(null);
  const rows = saved.data ?? [];

  const apply = (f: SavedFilter) => {
    const parsed = parseSavedFilter(f.advanced_filter);
    if (!parsed) {
      toast("error", "Apply Filter", "This saved filter could not be read.");
      return;
    }
    onApply({ ...withFilters(parsed, "saved"), active: true });
  };

  const accept = () => {
    if (!pending) return;
    const f = pending.filter;
    setPending(null);
    remove.mutate(f, {
      onSuccess: () => toast("success", "Delete Filter", "Your filter was deleted successfully."),
      onError: () => toast("error", "Delete Filter", "Your filter was not deleted, Please try again."),
    });
  };

  if (saved.isPending) {
    return (
      <div className="mt-3 flex justify-content-center">
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <>
      {rows.length === 0 && <div className="mt-3 text-center font-bold">{saved.isError ? "Your saved filters could not be loaded." : "No saved filters available"}</div>}
      {rows.length > 0 && (
        <div className="mt-3">
          {rows.map((f) => (
            <div key={f.id} className="p-4 mt-3 border-1 surface-border surface-card border-round flex flex-column">
              <div>
                <div className="flex flex-row align-items-center">
                  <span className="material-symbols-rounded color-primary">tune</span>
                  <div className="text-overflow-ellipsis font-bold ml-3">{(f.name ?? "").toUpperCase()}</div>
                </div>
                <div className="mt-2 w-full flex flex-row align-items-center justify-content-between gap-2">
                  <div>
                    <PButton label="Apply" icon="filter" className="font-bold min-width-100" sx={{ fontWeight: 700 }} onClick={() => apply(f)} />
                  </div>
                  <div>
                    <PButton
                      label="Delete"
                      icon="trash"
                      severity="danger"
                      className="font-bold min-width-100"
                      sx={{ fontWeight: 700 }}
                      loading={remove.isPending && remove.variables?.id === f.id}
                      onClick={(e) => setPending({ anchor: e.currentTarget, filter: f })}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmPopup
        anchorEl={pending?.anchor ?? null}
        ariaLabel="Delete filter"
        acceptLabel="Yes"
        rejectLabel="No"
        danger
        onHide={() => setPending(null)}
        onReject={() => setPending(null)}
        onAccept={accept}
      >
        <div className="flex flex-column align-items-center w-full gap-3 border-bottom-1 surface-border p-3 mb-3">
          <span className="text-red-500" style={{ display: "flex" }}>
            <PrimeGlyph name="exclamation-circle" size="3rem" />
          </span>
          <p>Are you sure you want to delete this filter?</p>
        </div>
      </ConfirmPopup>
    </>
  );
}
